import { useEffect, useMemo, useRef, useState } from 'react';
import { getGenericSigs } from '../../../../service/api/generic-sig';
import { getGenericSigSuggestion, normGeneric } from '../../../../utils/common-funtions/medicine-sig';

// The doctor's saved generic SIGs for a list of medicines (the prescribed rows),
// fetched in one request per set of generics. Returns
// Map(medicineId -> { presets, autoFill }): presets best first, autoFill the one
// that is safe to pre-fill (or null).
export const useGenericSigSuggestions = (medicines, doctorId, language) => {
    const [presetsByGeneric, setPresetsByGeneric] = useState(() => new Map());
    const requestRef = useRef(0);

    // Refetch only when the set of generics changes, not on every new array
    // (e.g. after editing a prescribed row's SIG).
    const genericsKey = [...new Set((medicines || []).map((medicine) => medicine?.generic?.trim()).filter(Boolean))]
        .sort()
        .join("\n");

    useEffect(() => {
        const generics = genericsKey ? genericsKey.split("\n") : [];
        const requestId = ++requestRef.current;
        if (!doctorId || !generics.length) {
            setPresetsByGeneric(new Map());
            return;
        }
        getGenericSigs(doctorId, { generics })
            .then((res) => {
                // a newer search already replaced these results
                if (requestId !== requestRef.current || res?.status !== 200) return;
                const map = new Map();
                (res.data || []).forEach((preset) => {
                    if (!map.has(preset.genericKey)) map.set(preset.genericKey, []);
                    map.get(preset.genericKey).push(preset);
                });
                setPresetsByGeneric(map);
            })
            .catch((error) => {
                console.log(error);
            });
    }, [genericsKey, doctorId]);

    return useMemo(() => {
        const suggestions = new Map();
        (medicines || []).forEach((medicine) => {
            const presets = presetsByGeneric.get(normGeneric(medicine?.generic)) || [];
            if (presets.length) suggestions.set(medicine?._id, getGenericSigSuggestion(presets, medicine, language));
        });
        return suggestions;
    }, [medicines, presetsByGeneric, language]);
};

// Fetches a medicine's saved generic SIGs BEFORE the SIG modal opens, so the modal
// is painted once: at its final width, with the list and any pre-fill already in
// place. (Loading them after it opened made it jump from narrow/empty to
// wide/filled.) `open(preloaded)` runs when they are ready — at once when the
// medicine has no generic (e.g. creating a new one).
export const useSigFormPreload = (doctorId, language) => {
    const [preloadedSigs, setPreloadedSigs] = useState(null);
    const requestRef = useRef(0);

    const preloadSigs = async (medicine, open) => {
        const requestId = ++requestRef.current;
        const generic = medicine?.generic?.trim() || "";
        let preloaded = { generic, presets: [], autoFill: null };
        if (doctorId && generic) {
            try {
                const res = await getGenericSigs(doctorId, { generic });
                if (res?.status === 200) {
                    preloaded = { generic, ...getGenericSigSuggestion(res.data || [], medicine, language) };
                }
            } catch (error) {
                console.log(error);
            }
        }
        // a newer click replaced this one
        if (requestId !== requestRef.current) return;
        setPreloadedSigs(preloaded);
        open(preloaded);
    };

    return { preloadedSigs, preloadSigs };
};
